import { useEffect, useRef, useState } from "react";

import { clsxMerge } from "shared/lib/helpers";
import {
  CallContextPanel,
  CallContextSaveStatusT,
} from "shared/ui/email/call-context-panel";
import {
  CALL_CONTEXT_SOURCES,
  CallContextSourceT,
} from "shared/lib/interfaces/email/call-context";
import { EmailCallContextPayloadI } from "shared/lib/interfaces/email/ai";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import {
  useFetchCallContext,
  useSaveCallContext,
} from "@/modules/email-templates-sidebar/queries";
import { useEmailTemplatesContext } from "@/modules/email-templates-sidebar/context";

export const CallContextSection = ({ className }: { className?: string }) => {
  const { campaign, account, focusedContact } = useAccountDetailsContext();
  const { setCallContextDraft } = useEmailTemplatesContext();

  const { data: callContextResponse, isLoading } = useFetchCallContext(
    campaign?.id,
    account?.id,
    focusedContact?.id
  );

  const { mutateAsync: saveCallContextAsync, isPending } = useSaveCallContext(
    campaign?.id,
    account?.id
  );

  const savedContext = callContextResponse?.data?.call_context ?? null;

  const [source, setSource] = useState<CallContextSourceT>(
    CALL_CONTEXT_SOURCES.TRANSCRIPT
  );
  const [content, setContent] = useState("");
  const [status, setStatus] = useState<CallContextSaveStatusT>("idle");

  /**
   * Publish whatever is in the textarea so Compose can send it without waiting
   * for Save. A rep who pastes a transcript and goes straight to Compose has
   * not saved yet, and generating from the empty saved row is the reason the
   * AI appeared to ignore the call entirely.
   *
   * Published at the write sites rather than from an effect on [contactId,
   * content]: an effect sees the new contact id in the same pass that the
   * hydrate below has only *queued* the new content, so it would publish this
   * contact's id against the previous contact's transcript for one render.
   *
   * Merged through a ref, and that is load-bearing rather than tidy. A paste
   * calls both setters in one tick - `applyIncomingText` does
   * `onChangeContent(trimmed)` then `onChangeSource(detected)` - and both
   * closures see the same pre-paste render. Publishing whole objects meant the
   * second call overwrote the first with its stale `content`, which on a paste
   * into an empty field is "": the transcript was dropped on exactly the path
   * this feature exists for. Patching a ref makes the two calls compose.
   */
  const draftRef = useRef<EmailCallContextPayloadI>({ source, content });

  const publishDraft = (
    contactId: string | undefined,
    patch: Partial<EmailCallContextPayloadI>
  ) => {
    draftRef.current = { ...draftRef.current, ...patch };

    if (contactId) {
      setCallContextDraft(contactId, draftRef.current);
    }
  };

  /**
   * Hydrate from whatever is already on the account. Keyed on the saved row's
   * id so it never stomps on what the SDR is currently typing, and on the
   * focused contact so switching contacts always re-resolves.
   *
   * The no-saved-context branch has to clear rather than bail out. This section
   * stays mounted when the sidebar is already open and the rep picks a
   * different contact, so returning early left the previous contact's
   * transcript sitting in state - with savedContent now "" the panel reads it
   * as unsaved edits, enables Save, and writes contact A's call under contact
   * B's id.
   */
  useEffect(() => {
    if (!savedContext) {
      setSource(CALL_CONTEXT_SOURCES.TRANSCRIPT);
      setContent("");
      setStatus("idle");
      publishDraft(focusedContact?.id, {
        source: CALL_CONTEXT_SOURCES.TRANSCRIPT,
        content: "",
      });

      return;
    }

    setSource(savedContext.source);
    setContent(savedContext.content);
    setStatus("saved");
    publishDraft(focusedContact?.id, {
      source: savedContext.source,
      content: savedContext.content,
    });
  }, [focusedContact?.id, savedContext?.id]);

  const handleSave = async () => {
    if (!focusedContact?.id) {
      return;
    }

    setStatus("saving");

    try {
      await saveCallContextAsync({
        contact_id: focusedContact.id,
        source,
        content,
      });

      setStatus("saved");
    } catch {
      setStatus("error");
    }
  };

  const handleRemove = () => {
    setContent("");
    setStatus("idle");
    publishDraft(focusedContact?.id, { content: "" });
  };

  if (isLoading) {
    return (
      <div
        className={clsxMerge(
          "h-[92px] animate-pulse rounded-lg bg-black/5",
          className
        )}
      />
    );
  }

  return (
    <CallContextPanel
      className={className}
      source={source}
      content={content}
      savedContent={savedContext?.content || ""}
      status={isPending ? "saving" : status}
      onChangeSource={(value) => {
        setSource(value);
        publishDraft(focusedContact?.id, { source: value });
      }}
      onChangeContent={(value) => {
        setContent(value);
        setStatus("idle");
        publishDraft(focusedContact?.id, { content: value });
      }}
      onSave={handleSave}
      onClear={handleRemove}
    />
  );
};
